import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type ComposerSubmitBehaviour,
    type ComposerAttachment,
    type NCoreUIKitIcon,
    ToggleGroup,
    Composer,
    Button,
    Bubble,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Star as StarGlyph
} from "lucide-react-native";
import {
    InlineCode,
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    ChevronRightIcon,
    PlusIcon
} from "../../anatomy/icons";

const NAME = "Composer";

const USAGE_CODE = `const [message, setMessage] = useState("");

const handleSubmit = (text: string, files: Array<ComposerAttachment>) => {
    send(text, files);

    setMessage("");
};

<Composer
    placeholder="Type a message…"
    onChangeText={setMessage}
    onSubmit={handleSubmit}
    value={message}
/>`;

const SEND_CODE = `<Composer
    submitBehaviour="auto"
    onEditLastPress={editLast}
    onSubmit={send}
/>`;

const ATTACHMENTS_CODE = `const [files, setFiles] = useState<Array<ComposerAttachment>>([]);

<Composer
    onAttachmentsChange={setFiles}
    accept="image/*,.pdf"
    maxAttachments={4}
    attachments={files}
    onSubmit={send}
/>`;

const STATES_CODE = `<Composer
    isStreaming={isStreaming}
    onStopPress={stop}
/>

<Composer
    isOffline={!isConnected}
/>

<Composer
    disabledText="Only admins can write here"
    isDisabled={true}
/>

<Composer
    maxLength={500}
/>`;

const PARTS_CODE = `<Composer>
    <Composer.Input
        placeholder="Ask anything"
    />
    <Composer.Actions>
        <Composer.Action
            onPress={dictate}
            title="Dictate"
            icon={MicIcon}
        />
    </Composer.Actions>
    <Composer.Submit
        icon={SendIcon}
        title="Send"
    />
</Composer>`;

const BEHAVIOURS: Array<ComposerSubmitBehaviour> = [
    "auto",
    "enter",
    "button"
];

const SIZES = [
    "medium",
    "large"
] as const;

const UPLOAD_STEP = 20;

const UPLOAD_DELAY = 500;

const STREAM_DELAY = 3000;

const MAX_LENGTH = 40;

const MAX_ATTACHMENTS = 4;

const STACK_ITEM_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 8
};

const createAttachments = (t: DocsTranslate): Array<ComposerAttachment> => [
    {
        description: t("sample.fileSize"),
        title: t("sample.fileName"),
        uploadState: "done",
        id: "report"
    },
    {
        title: t("composerPage.fileSheet"),
        uploadState: "uploading",
        id: "budget",
        progress: 20
    },
    {
        errorText: t("composerPage.errorReason"),
        title: t("composerPage.fileVideo"),
        uploadState: "error",
        id: "clip"
    }
];

const advanceUpload = (item: ComposerAttachment): ComposerAttachment => {
    if(item.uploadState !== "uploading") {
        return item;
    }

    const progress = (item.progress ?? 0) + UPLOAD_STEP;

    if(progress >= 100) {
        return {
            ...item,
            uploadState: "done",
            progress: 100
        };
    }

    return {
        ...item,
        progress
    };
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("composerPage.sendTitle"),
        id: "sending"
    },
    {
        title: t("composerPage.attachmentsTitle"),
        id: "attachments"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("composerPage.partsTitle"),
        id: "parts"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ComposerPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        message,
        setMessage
    ] = useState("");

    const [
        sent,
        setSent
    ] = useState<Array<string>>([]);

    const [
        behaviour,
        setBehaviour
    ] = useState<ComposerSubmitBehaviour>("auto");

    const [
        lastSent,
        setLastSent
    ] = useState<string | undefined>(undefined);

    const [
        attachments,
        setAttachments
    ] = useState<Array<ComposerAttachment>>(() => createAttachments(t));

    const [
        sentCount,
        setSentCount
    ] = useState<number | undefined>(undefined);

    const [
        isStreaming,
        setIsStreaming
    ] = useState(false);

    const [
        isStarred,
        setIsStarred
    ] = useState(false);

    useEffect(() => {
        if(!attachments.some((item) => item.uploadState === "uploading")) {
            return;
        }

        const timer = window.setTimeout(() => setAttachments((current) => current.map(advanceUpload)), UPLOAD_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        attachments
    ]);

    useEffect(() => {
        if(!isStreaming) {
            return;
        }

        const timer = window.setTimeout(() => setIsStreaming(false), STREAM_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isStreaming
    ]);

    const handleSubmit = (text: string) => {
        setSent((current) => [
            ...current.slice(-2),
            text
        ]);

        setMessage("");
    };

    const handleBehaviourChange = (value: string | Array<string>) => {
        const next = BEHAVIOURS.find((item) => item === value);

        if(next) {
            setBehaviour(next);
        }
    };

    const handleBehaviourSubmit = (text: string) => setLastSent(text);

    const handleRetry = (id: string) => setAttachments((current) => current.map((item) => item.id === id ? {
        ...item,
        uploadState: "uploading",
        errorText: undefined,
        progress: 0
    } : item));

    const handleAttachmentsSubmit = (_: string, files: Array<ComposerAttachment>) => {
        setSentCount(files.length);

        setAttachments([]);
    };

    const handleRestore = () => {
        setSentCount(undefined);

        setAttachments(createAttachments(t));
    };

    const handleStreamSubmit = () => setIsStreaming(true);

    const handleStreamStop = () => setIsStreaming(false);

    const handleStarPress = () => setIsStarred(!isStarred);

    const renderStarIcon: NCoreUIKitIcon = ({
        customColor,
        size
    }) => <StarGlyph
        fill={isStarred ? customColor : "none"}
        color={customColor}
        size={size}
    />;

    const items = attachments.map((item) => item.uploadState === "error" ? {
        ...item,
        onRetry: () => handleRetry(item.id)
    } : item);

    const renderState = (label: string, node: ReactNode) => <div
        style={STACK_ITEM_STYLE}
        key={label}
    >
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
        {node}
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("composerPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("composerPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("composerPage.overviewAlternatives")}
            </Paragraph>
            <Preview
                caption={t("composerPage.overviewCaption")}
                isStacked={true}
            >
                {
                    sent.map((text, index) => <Bubble
                        variant="user"
                        key={index}
                    >
                        {text}
                    </Bubble>)
                }
                <Composer
                    placeholder={t("composerPage.placeholder")}
                    onChangeText={setMessage}
                    onSubmit={handleSubmit}
                    value={message}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("composerPage.sendTitle")}
            id="sending"
        >
            <Paragraph>
                {t("composerPage.sendText")}
            </Paragraph>
            <Preview
                caption={lastSent ? t("composerPage.sendSent", [
                    lastSent
                ]) : t("composerPage.sendHint")}
                isStacked={true}
            >
                <ToggleGroup
                    onValueChange={handleBehaviourChange}
                    title={t("composerPage.behaviour")}
                    attachBehaviour="attached"
                    isClearEnabled={false}
                    variant="outline"
                    value={behaviour}
                    size="small"
                >
                    {
                        BEHAVIOURS.map((item) => <ToggleGroup.Item
                            title={item}
                            value={item}
                            key={item}
                        />)
                    }
                </ToggleGroup>
                <Composer
                    placeholder={t("composerPage.placeholder")}
                    onSubmit={handleBehaviourSubmit}
                    submitBehaviour={behaviour}
                />
            </Preview>
            <CodeBlock
                code={SEND_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("composerPage.attachmentsTitle")}
            id="attachments"
        >
            <Paragraph>
                {t("composerPage.attachmentsText")}
            </Paragraph>
            <Preview
                caption={sentCount === undefined ? t("composerPage.attachmentsHint") : t("composerPage.attachmentsSent", [
                    sentCount
                ])}
                isStacked={true}
            >
                <Composer
                    placeholder={t("composerPage.placeholder")}
                    onAttachmentsChange={setAttachments}
                    onSubmit={handleAttachmentsSubmit}
                    maxAttachments={MAX_ATTACHMENTS}
                    attachments={items}
                />
                <Button
                    isDisabled={attachments.length > 0}
                    title={t("composerPage.restore")}
                    onPress={handleRestore}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={ATTACHMENTS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("composerPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("composerPage.statesCaption")}
                isStacked={true}
            >
                {
                    renderState("isStreaming", <Composer
                        placeholder={t("composerPage.placeholder")}
                        defaultValue={t("sample.reply")}
                        onStopPress={handleStreamStop}
                        onSubmit={handleStreamSubmit}
                        isStreaming={isStreaming}
                    />)
                }
                {
                    renderState("isOffline", <Composer
                        defaultValue={t("composerPage.offlineText")}
                        placeholder={t("composerPage.placeholder")}
                        isOffline={true}
                    />)
                }
                {
                    renderState("isDisabled", <Composer
                        placeholder={t("composerPage.disabledPlaceholder")}
                        disabledText={t("composerPage.disabledText")}
                        isDisabled={true}
                    />)
                }
                {
                    renderState(`maxLength={${MAX_LENGTH}}`, <Composer
                        placeholder={t("composerPage.placeholder")}
                        defaultValue={t("composerPage.limitText")}
                        maxLength={MAX_LENGTH}
                    />)
                }
            </Preview>
            <CodeBlock
                code={STATES_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("composerPage.partsTitle")}
            id="parts"
        >
            <Paragraph>
                {t("composerPage.partsText")}
            </Paragraph>
            <Preview
                caption={t("composerPage.partsCaption")}
                isStacked={true}
            >
                <Composer>
                    <Composer.Input
                        placeholder={t("composerPage.partsPlaceholder")}
                    />
                    <Composer.Actions>
                        <Composer.Action
                            title={t("composerPage.actionAdd")}
                            onPress={() => undefined}
                            icon={PlusIcon}
                        />
                        <Composer.Action
                            title={t("composerPage.actionStar")}
                            onPress={handleStarPress}
                            icon={renderStarIcon}
                            isPressed={isStarred}
                        />
                    </Composer.Actions>
                    <Composer.Submit
                        title={t("composerPage.submitTitle")}
                        icon={ChevronRightIcon}
                    />
                </Composer>
            </Preview>
            <CodeBlock
                code={PARTS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("composerPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => renderState(`size="${size}"`, <Composer
                        placeholder={t("composerPage.placeholder")}
                        size={size}
                    />))
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("composerPage.a11yGroup"),
                    t("composerPage.a11ySubmit"),
                    t("composerPage.a11yKeyboard"),
                    t("composerPage.a11yHints"),
                    t("composerPage.a11yAttachments"),
                    t("composerPage.a11yActions"),
                    t("composerPage.a11yParts")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "(text: string, attachments: Array<ComposerAttachment>) => void",
                        description: t("composerPage.propOnSubmit"),
                        name: "onSubmit"
                    },
                    {
                        description: t("composerPage.propValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("composerPage.propDefaultValue"),
                        defaultValue: "\"\"",
                        name: "defaultValue",
                        type: "string"
                    },
                    {
                        description: t("composerPage.propOnChangeText"),
                        type: "(text: string) => void",
                        name: "onChangeText"
                    },
                    {
                        description: t("composerPage.propPlaceholder"),
                        name: "placeholder",
                        type: "string"
                    },
                    {
                        description: t("composerPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("composerPage.propSubmitBehaviour"),
                        type: "\"auto\" | \"enter\" | \"button\"",
                        defaultValue: "\"auto\"",
                        name: "submitBehaviour"
                    },
                    {
                        description: t("composerPage.propSize"),
                        type: "\"medium\" | \"large\"",
                        name: "size"
                    },
                    {
                        description: t("composerPage.propMaxLength"),
                        name: "maxLength",
                        type: "number"
                    },
                    {
                        description: t("composerPage.propMaxRows"),
                        defaultValue: "8",
                        name: "maxRows",
                        type: "number"
                    },
                    {
                        description: t("composerPage.propAttachments"),
                        type: "Array<ComposerAttachment>",
                        defaultValue: "[]",
                        name: "attachments"
                    },
                    {
                        type: "(attachments: Array<ComposerAttachment>) => void",
                        description: t("composerPage.propOnAttachmentsChange"),
                        name: "onAttachmentsChange"
                    },
                    {
                        description: t("composerPage.propOnAttachmentPress"),
                        type: "(attachment: ComposerAttachment) => void",
                        name: "onAttachmentPress"
                    },
                    {
                        description: t("composerPage.propOnAttachPress"),
                        name: "onAttachPress",
                        type: "() => void"
                    },
                    {
                        description: t("composerPage.propAccept"),
                        type: "string",
                        name: "accept"
                    },
                    {
                        description: t("composerPage.propMaxAttachments"),
                        name: "maxAttachments",
                        type: "number"
                    },
                    {
                        description: t("composerPage.propIsStreaming"),
                        defaultValue: "false",
                        name: "isStreaming",
                        type: "boolean"
                    },
                    {
                        description: t("composerPage.propOnStopPress"),
                        name: "onStopPress",
                        type: "() => void"
                    },
                    {
                        description: t("composerPage.propOnEditLastPress"),
                        name: "onEditLastPress",
                        type: "() => void"
                    },
                    {
                        description: t("composerPage.propIsOffline"),
                        defaultValue: "false",
                        name: "isOffline",
                        type: "boolean"
                    },
                    {
                        description: t("composerPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("composerPage.propDisabledText"),
                        name: "disabledText",
                        type: "string"
                    },
                    {
                        description: t("composerPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("composerPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("composerPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("composerPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Composer.Submit
                </InlineCode>
                {" "}
                {t("composerPage.submitPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("composerPage.submitIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("composerPage.submitStopIcon"),
                        type: "NCoreUIKitIcon",
                        name: "stopIcon"
                    },
                    {
                        description: t("composerPage.submitTitleProp"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("composerPage.submitStopTitle"),
                        name: "stopTitle",
                        type: "string"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Composer.Action
                </InlineCode>
                {" "}
                {t("composerPage.actionPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("composerPage.actionTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("composerPage.actionIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("composerPage.actionOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("composerPage.actionIsPressed"),
                        name: "isPressed",
                        type: "boolean"
                    },
                    {
                        description: t("composerPage.actionIsDisabled"),
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("composerPage.actionIsRefocusEnabled"),
                        name: "isRefocusEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Composer.Attachments
                </InlineCode>
                {" "}
                {t("composerPage.attachmentsPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("composerPage.attachmentsLayout"),
                        type: "\"scroll\" | \"wrap\"",
                        defaultValue: "\"scroll\"",
                        name: "layoutBehaviour"
                    },
                    {
                        description: t("composerPage.attachmentsChildren"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("composerPage.partsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ComposerPage;
